import type { SoundPack, SoundEvent } from '../audio/sound-pack';
import { POWER_UP_KINDS, type PowerUpKind } from '../../shared/power-ups';
import type { PowerUpTileProperty } from './power-up-tokens';

/** Visual geometry only: board spacing, hit targets and game timing belong to the app. */
export const DEFAULT_TOKENS = {
  background: '#ffffff',
  foreground: '#000000',
  surface: '#ffffff',
  surfaceText: '#000000',
  mutedText: '#000000',
  border: '#000000',
  accent: '#000000',
  primary: '#ffffff',
  primaryText: '#000000',
  selected: '#000000',
  selectedText: '#ffffff',
  tile: '#ffffff',
  tileText: '#000000',
  tileSelected: '#000000',
  tileSelectedText: '#ffffff',
  tileHead: '#ffffff',
  tileHeadText: '#000000',
  success: '#000000',
  danger: '#000000',
  highlight: '#ffffff',
  path: '#00000080',
  overlay: '#00000059',
  focus: '#000000',
  font: 'Arial, Helvetica, sans-serif',
  boardFont: 'Arial, Helvetica, sans-serif',
  displayFont: 'Arial, Helvetica, sans-serif',
  fontWeight: '500',
  headingWeight: '700',
  fieldFont: 'var(--theme-font)',
  fieldFontSize: '17px',
  fieldFontWeight: '600',
  fieldFontStyle: 'normal',
  fieldLetterSpacing: 'var(--theme-letter-spacing)',
  fieldLineHeight: 'normal',
  wordChipFontSize: '14px',
  wordChipFontWeight: '600',
  wordChipScoreWeight: '700',
  buttonFont: 'var(--theme-font)',
  buttonFontWeight: '700',
  buttonFontStyle: 'normal',
  buttonFontSize: '15px',
  buttonSmallFontSize: '13px',
  buttonBigFontSize: '17px',
  buttonLetterSpacing: 'var(--theme-letter-spacing)',
  buttonLineHeight: 'normal',
  buttonTextTransform: 'none',
  buttonLabelFontSize: '19px',
  buttonLabelFontWeight: 'var(--theme-button-font-weight)',
  buttonLabelLetterSpacing: '-0.4px',
  letterSpacing: 'normal',
  surfaceShadow: 'none',
  controlShadow: 'none',
  tileShadow: 'none',
  cardRadius: '20px',
  buttonRadius: '14px',
  tileRadius: '20%',
  inputRadius: '12px',
  chipRadius: '10px',
  dialogRadius: '20px',
  sheetRadius: '24px 24px 0 0',
  cardBorder: '1px solid var(--theme-border)',
  buttonBorder: '1px solid var(--theme-border)',
  tileBorder: '1px solid var(--theme-border)',
  inputBorder: '2px solid var(--theme-border)',
  buttonBackground: 'var(--theme-surface)',
  inputBackground: 'var(--theme-surface)',
  dialogShadow: 'var(--theme-surface-shadow)',
  dialogBackground: 'var(--theme-surface)',
  dialogText: 'var(--theme-surface-text)',
  dialogTexture: 'none',
  dialogTextureSize: 'cover',
  cardTexture: 'none',
  tileTexture: 'none',
  buttonTexture: 'none',
  cardTextureSize: 'auto',
  buttonTextureSize: 'auto',
  tileTextureSize: '100% 100%',
  hudBackground: 'transparent',
  feedbackBackground: 'transparent',
  buttonHover: 'var(--theme-highlight)',
  buttonHoverText: 'var(--theme-surface-text)',
  primaryHover: 'var(--theme-primary)',
  primaryHoverText: 'var(--theme-primary-text)',
  ghostHover: 'var(--theme-button-hover)',
  ghostHoverText: 'var(--theme-button-hover-text)',
  destructiveBackground: 'var(--theme-button-background)',
  destructiveText: 'var(--theme-surface-text)',
  destructiveHover: 'var(--theme-button-hover)',
  destructiveHoverText: 'var(--theme-button-hover-text)',
  buttonPress: 'translateY(3px)',
  buttonHoverTransform: 'none',
  tileSelectTransform: 'scale(0.92) translateY(3px)',
  tileSelectedShadow: 'none',
  tileHeadOutline: '2px solid var(--theme-accent)',
  tileHeadShadow: 'none',
  disabledOpacity: '0.45',
  focusWidth: '2px',
  overlayBlur: '2px',
  pathWidth: '4',
  pathDash: 'none',
  pathGlow: 'none',
  pathAnimation: 'none',
  interactionDuration: '90ms',
  interactionEasing: 'ease',
  spinnerTrack: 'color-mix(in srgb, var(--theme-foreground) 20%, transparent)',
  spinnerColor: 'var(--theme-accent)',
  connectionDisconnected: 'var(--theme-muted-text)',
  connectionConnected: '#278044',
  connectionReconnecting: '#b58a00',
  connectionReconnectingAnimation: 'spellie-connection-pulse 1.4s ease-in-out infinite',
  podiumGold: '#b8860b',
  podiumSilver: '#80868f',
  podiumBronze: '#a0522d',
  winnerBackground: 'color-mix(in srgb, var(--theme-podium-gold) 10%, var(--theme-surface))',
  winnerBorder: '2px solid var(--theme-podium-gold)',
  winnerShadow: 'none',
  spinnerRadius: '50%',
  spinnerAnimation: 'spellie-spinner 0.8s linear infinite',
  enterAnimation: 'none',
  revealAnimation: 'none',
  selectAnimation: 'none',
  countdownAnimation: 'none',
  urgentAnimation: 'pulse 0.5s ease-in-out infinite alternate',
  acceptAnimation: 'spellie-accept 240ms ease-out both',
  powerUpFeedbackColor: 'var(--theme-accent)',
  powerUpFeedbackBorder: '3px solid currentColor',
  powerUpFeedbackShadow: '0 0 0 2px var(--theme-surface)',
  powerUpCollectAnimation: 'spellie-power-up-collect 600ms ease-out',
  powerUpHitAnimation: 'spellie-power-up-hit 600ms ease-out',
  powerUpBlockedAnimation: 'spellie-power-up-blocked 600ms ease-out',
  rejectAnimation: 'spellie-reject 350ms ease both',
  duplicateAnimation: 'spellie-duplicate 300ms ease both',
  resultsAnimation: 'none',
  dialogAnimation: 'sheet-up 220ms ease-out',
  welcomeAnimation: 'none',
  decorationAnimation: 'none',
  titleShadow: 'none',
  titleTransform: 'none',
  backgroundFilter: 'none',
} as const;
export type ThemeTokens = { readonly [K in keyof typeof DEFAULT_TOKENS]: string };

export const DEFAULT_ICONS = {
  home: '⌂',
  settings: '⚙',
  back: '←',
  next: '→',
  skip: '»',
  daily: '☀',
  competitive: '✪',
  solo: '✎',
  versus: '⚔',
  trophy: '♜',
  crown: '♔',
  dice: '⚄',
  coffee: '☕',
  rules: '▤',
  bug: '🐞',
  trash: '🗑',
  logo: '✧',
  close: '×',
  share: '⤴',
} as const;
export type IconName = keyof typeof DEFAULT_ICONS;

/**
 * How a tile looks while a chaos power-up sits on it. Every property is a CSS value applied
 * to the whole tile, so a pack can recolor, texture, reshape and animate it; `glyph` is a CSS
 * `content` value (quoted text or `url()`), drawn in a corner by the board. Selecting the tile
 * restores the pack's selected/head styling.
 */
export type PowerUpTileStyle = { readonly [P in PowerUpTileProperty]: string };
export type PowerUpTileStyles = Record<PowerUpKind, PowerUpTileStyle>;
const POWER_UP_TILE_BASE: Omit<PowerUpTileStyle, 'glyph'> = {
  glyphColor: 'var(--theme-accent)',
  background: 'var(--theme-tile-texture), var(--theme-tile)',
  backgroundSize: 'var(--theme-tile-texture-size), auto',
  text: 'var(--theme-tile-text)',
  border: '3px solid var(--theme-accent)',
  shadow: 'var(--theme-tile-shadow)',
  transform: 'none',
  animation: 'spellie-power-up-pulse 1.2s ease-in-out infinite',
};
const DEFAULT_POWER_UP_GLYPHS: Record<PowerUpKind, string> = {
  gold: '★',
  double: '×2',
  reveal: '◉',
  shield: '⛨',
  freeze: '❄',
  tax: '½',
  spin: '↻',
  fog: '☁',
};
export const DEFAULT_POWER_UP_TILES: PowerUpTileStyles = Object.fromEntries(
  POWER_UP_KINDS.map((kind) => [
    kind,
    { ...POWER_UP_TILE_BASE, glyph: JSON.stringify(DEFAULT_POWER_UP_GLYPHS[kind]) },
  ]),
) as PowerUpTileStyles;
export type ThemeIcon = { readonly src: string } | { readonly text: string };
export interface BackgroundLayer {
  readonly src: string;
  readonly mobileSrc?: string;
  readonly opacity?: number;
  /** Maximum mouse parallax displacement in pixels. Disabled for touch/reduced motion. */
  readonly depth?: number;
  readonly size?: 'cover' | 'contain';
  readonly position?: string;
  /** Globally namespaced pack keyframes; decorative only. */
  readonly animation?: string;
}
export interface ThemeDefinition {
  readonly id: string;
  readonly name: string;
  readonly description?: string;
  readonly credits?: string;
  readonly license?: string;
  readonly preview?: string;
  readonly colorScheme?: 'light' | 'dark';
  readonly tokens?: Partial<ThemeTokens>;
  readonly icons?: Partial<Record<IconName, ThemeIcon>>;
  readonly background?: readonly BackgroundLayer[];
  readonly sounds?: Readonly<SoundPack>;
  readonly haptics?: Partial<Record<SoundEvent, readonly number[]>>;
  /** Per-kind chaos tile treatments; unspecified properties resolve from the defaults above. */
  readonly powerUps?: Partial<Record<PowerUpKind, Partial<PowerUpTileStyle>>>;
}
export interface Theme extends ThemeDefinition {
  readonly colorScheme: 'light' | 'dark';
  readonly tokens: ThemeTokens;
  readonly icons: Record<IconName, ThemeIcon>;
  readonly background: readonly BackgroundLayer[];
  readonly sounds: Readonly<SoundPack>;
  readonly haptics: Partial<Record<SoundEvent, readonly number[]>>;
  readonly powerUps: PowerUpTileStyles;
}

/** Every pack resolves from Minimal, never from the previously selected pack. */
export function defineTheme(definition: ThemeDefinition): Theme {
  const icons = Object.fromEntries(
    Object.entries(DEFAULT_ICONS).map(([key, text]) => [key, { text }]),
  ) as Record<IconName, ThemeIcon>;
  return {
    ...definition,
    colorScheme: definition.colorScheme ?? 'light',
    tokens: { ...DEFAULT_TOKENS, ...definition.tokens },
    icons: { ...icons, ...definition.icons },
    background: [...(definition.background ?? [])],
    sounds: { ...definition.sounds },
    haptics: { ...definition.haptics },
    powerUps: Object.fromEntries(
      POWER_UP_KINDS.map((kind) => [
        kind,
        { ...DEFAULT_POWER_UP_TILES[kind], ...definition.powerUps?.[kind] },
      ]),
    ) as PowerUpTileStyles,
  };
}
